import {
	completedAtTransition,
	dayNodeId,
	defaultStatusFor,
	generateKeyBetween,
	generateNKeysBetween,
	isStatusAllowed,
	normalizeSelection,
	normalizeTags,
	type PlainDate,
	SYSTEM_NODE_IDS,
	tagId
} from "@mapcraft-tasks/domain";
import { containerRows, type Replica, subtreeIds } from "./replica";
import type { Command, CommandLocal, Node, SubtreeDraft } from "./types";

/**
 * Наивное локальное применение команд.
 *
 * Редьюсер НАМЕРЕННО не проверяет инварианты: он предположение, арбитр — сервер.
 * Ошибся — операция отклонится, и изменение исчезнет само (offline-sync §4, §11).
 * Дублируется здесь не логика домена, а её применение, и оно тривиально; всё, что
 * действительно общее (позиции, статусы, нормализация выделения, id дней),
 * берётся из `packages/domain` — тогда сторонам нечем разъехаться.
 */

export type ReducerEnv = {
	opId: string;
	now: Date;
	today: PlainDate;
	/** Данные, приложенные к операции для локального применения (см. `CommandLocal`). */
	local?: CommandLocal;
};

function place(
	replica: Replica,
	containerId: string,
	anchor: { after?: string | null; before?: string | null } | undefined,
	count = 1
): string[] {
	const rows = containerRows(replica, containerId);
	const index = (id: string | null | undefined) => (id ? rows.findIndex((r) => r.id === id) : -1);

	let prev: string | null = null;
	let next: string | null = null;

	const afterIdx = index(anchor?.after);
	const beforeIdx = index(anchor?.before);

	if (afterIdx >= 0) {
		prev = rows[afterIdx]!.position;
		next = rows[afterIdx + 1]?.position ?? null;
	} else if (beforeIdx >= 0) {
		prev = rows[beforeIdx - 1]?.position ?? null;
		next = rows[beforeIdx]!.position;
	} else {
		// Ни after, ни before — в конец контейнера.
		prev = rows[rows.length - 1]?.position ?? null;
	}

	return generateNKeysBetween(prev, next, count);
}

function patch(replica: Replica, nodeId: string, changes: Partial<Node>, now: Date): void {
	const node = replica.nodes.get(nodeId);
	if (!node) {
		return;
	}
	replica.nodes.set(nodeId, { ...node, ...changes, updatedAt: now });
}

function blankNode(input: {
	id: string;
	parentId: string;
	position: string;
	title: string;
	role?: Node["role"];
	body?: string | null;
	now: Date;
}): Node {
	const role = input.role ?? "plain";
	return {
		id: input.id,
		parentId: input.parentId,
		position: input.position,
		role,
		title: input.title,
		body: input.body ?? null,
		// Матрица role/status: у plain и системных статуса нет вообще (engineering §8.1).
		status: defaultStatusFor(role),
		scheduledDate: null,
		dayDate: null,
		completedAt: null,
		archivedAt: null,
		archivedOp: null,
		recurrence: null,
		xp: null,
		difficulty: null,
		tags: [],
		contentVersion: 1,
		createdAt: input.now,
		updatedAt: input.now
	};
}

function insertDrafts(
	replica: Replica,
	parentId: string,
	drafts: readonly SubtreeDraft[],
	anchor: { after?: string | null; before?: string | null } | undefined,
	now: Date
): void {
	const keys = place(replica, parentId, anchor, drafts.length);
	drafts.forEach((draft, i) => {
		replica.nodes.set(
			draft.id,
			blankNode({ id: draft.id, parentId, position: keys[i]!, title: draft.title, body: draft.body, now })
		);
		if (draft.children && draft.children.length > 0) {
			insertDrafts(replica, draft.id, draft.children, undefined, now);
		}
	});
}

export function applyCommand(replica: Replica, command: Command, env: ReducerEnv): void {
	const { now, today, opId, local } = env;

	switch (command.name) {
		case "createNode": {
			const { id, parentId, title, role, body } = command.input;
			const [position] = place(replica, parentId, command.input.place);
			replica.nodes.set(id, blankNode({ id, parentId, position: position!, title, role, body, now }));
			return;
		}

		case "insertSubtree": {
			insertDrafts(replica, command.input.parentId, command.input.nodes, command.input.place, now);
			return;
		}

		case "renameNode": {
			patch(replica, command.input.nodeId, { title: command.input.title }, now);
			return;
		}

		case "setBody": {
			patch(replica, command.input.nodeId, { body: command.input.body }, now);
			return;
		}

		case "moveNodes": {
			const ids = normalizeSelection(command.input.nodeIds, (id) => replica.nodes.get(id)?.parentId ?? null);
			if (ids.length === 0) {
				return;
			}
			const first = replica.nodes.get(ids[0]!);
			const parentId = command.input.parentId ?? first?.parentId ?? null;
			if (parentId === null) {
				return;
			}
			const keys = place(
				replica,
				parentId,
				{ after: command.input.after, before: command.input.before },
				ids.length
			);
			ids.forEach((id, i) => {
				patch(replica, id, { parentId, position: keys[i]! }, now);
			});
			return;
		}

		case "archiveNodes": {
			// Волна: ОДИН `archived_op` на всю пачку — иначе restore вернёт одну ноду из пяти.
			for (const id of subtreeIds(replica, command.input.nodeIds)) {
				const node = replica.nodes.get(id);
				if (node && node.archivedAt === null) {
					patch(replica, id, { archivedAt: now, archivedOp: opId }, now);
				}
			}
			return;
		}

		case "restoreNodes": {
			for (const rootId of command.input.nodeIds) {
				const root = replica.nodes.get(rootId);
				if (!root || root.archivedOp === null) {
					continue;
				}
				const wave = root.archivedOp;
				for (const id of subtreeIds(replica, [rootId])) {
					const node = replica.nodes.get(id);
					if (node?.archivedOp === wave) {
						patch(replica, id, { archivedAt: null, archivedOp: null }, now);
					}
				}
			}
			return;
		}

		case "deleteNodes": {
			const doomed = new Set(subtreeIds(replica, command.input.nodeIds));
			for (const id of doomed) {
				replica.nodes.delete(id);
			}
			// Рёбра уходят по FK на сервере; локально их надо убрать руками, иначе
			// они провисят до ближайшей дельты и попадут в выборки как «недоступно».
			for (const [id, p] of replica.placements) {
				if (doomed.has(p.nodeId) || doomed.has(p.containerId)) {
					replica.placements.delete(id);
				}
			}
			for (const [id, l] of replica.links) {
				if (doomed.has(l.fromNodeId) || doomed.has(l.toNodeId)) {
					replica.links.delete(id);
				}
			}
			for (const [id, c] of replica.completions) {
				if (doomed.has(c.nodeId)) {
					replica.completions.delete(id);
				}
			}
			return;
		}

		case "restoreDeleted": {
			// Правду знает сервер — у него слепок полнее (выполнения без окна в 90 дней,
			// меншены, сверенные с текстом). Здесь только правдоподобная картинка на время
			// до первого `pull`, и собирается она из того, что видела реплика при удалении.
			// Снимка нет (команда пересобрана из карантина) — молчим, дерево принесёт дельта.
			if (!local) {
				return;
			}
			for (const node of local.snapshot.nodes) {
				replica.nodes.set(node.id, node);
			}
			for (const placement of local.snapshot.placements) {
				replica.placements.set(placement.id, placement);
			}
			for (const link of local.snapshot.links) {
				replica.links.set(link.id, link);
			}
			for (const completion of local.snapshot.completions) {
				replica.completions.set(completion.id, completion);
			}
			return;
		}

		case "addPlacement": {
			const [position] = place(replica, command.input.containerId, command.input.place);
			const id = `pending:${opId}`;
			replica.placements.set(id, {
				id,
				nodeId: command.input.nodeId,
				containerId: command.input.containerId,
				position: position!,
				createdAt: now
			});
			return;
		}

		case "removePlacement": {
			for (const [id, p] of replica.placements) {
				if (p.nodeId === command.input.nodeId && p.containerId === command.input.containerId) {
					replica.placements.delete(id);
				}
			}
			return;
		}

		case "movePlacement": {
			const placement = replica.placements.get(command.input.placementId);
			if (!placement) {
				return;
			}
			const [position] = place(replica, placement.containerId, {
				after: command.input.after,
				before: command.input.before
			});
			replica.placements.set(placement.id, { ...placement, position: position! });
			return;
		}

		case "setStatusMany": {
			for (const id of command.input.nodeIds) {
				const node = replica.nodes.get(id);
				if (!node) {
					continue;
				}
				if (command.input.skipTerminal && (node.status === "done" || node.status === "cancelled")) {
					continue;
				}
				if (!isStatusAllowed(node.role, command.input.status)) {
					continue;
				}
				const transition = completedAtTransition(node.status, command.input.status);
				patch(
					replica,
					id,
					{
						status: command.input.status,
						completedAt: transition === "set" ? now : transition === "clear" ? null : node.completedAt
					},
					now
				);
			}
			return;
		}

		case "convertRole": {
			const node = replica.nodes.get(command.input.nodeId);
			if (!node) {
				return;
			}
			const role = command.input.role;
			const status = isStatusAllowed(role, node.status) ? node.status : defaultStatusFor(role);
			patch(replica, node.id, { role, status, completedAt: status === "done" ? node.completedAt : null }, now);
			return;
		}

		case "schedule": {
			patch(replica, command.input.nodeId, { scheduledDate: command.input.date }, now);
			return;
		}

		case "scheduleForToday": {
			patch(replica, command.input.nodeId, { scheduledDate: today }, now);
			return;
		}

		case "setDifficulty": {
			patch(replica, command.input.nodeId, { difficulty: command.input.difficulty }, now);
			return;
		}

		case "setRecurrence": {
			const recurrence = command.input.recurrence;
			// Инвариант `(role='recurring') = (recurrence IS NOT NULL)` держится одной операцией:
			// разводить роль и атрибут по разным командам значило бы уметь нарушить CHECK.
			patch(
				replica,
				command.input.nodeId,
				recurrence === null
					? { recurrence: null, role: "task", status: "open" }
					: { recurrence, role: "recurring", status: "open", completedAt: null },
				now
			);
			return;
		}

		case "setTags": {
			patch(replica, command.input.nodeId, { tags: normalizeTags(command.input.tags) }, now);
			return;
		}

		case "setTagColor": {
			const [name] = normalizeTags([command.input.tag]);
			if (!name) {
				return;
			}
			const id = tagId(name);
			if (command.input.color === null) {
				replica.tags.delete(id);
			} else {
				replica.tags.set(id, { id, name, color: command.input.color, updatedAt: now });
			}
			return;
		}

		case "completeNode": {
			const node = replica.nodes.get(command.input.nodeId);
			if (!node) {
				return;
			}
			if (node.role === "recurring") {
				// Статус НЕ трогаем: каждое «сделал» — отдельная строка события.
				const id = `pending:${opId}`;
				replica.completions.set(id, {
					id,
					nodeId: node.id,
					completedOn: command.input.on ?? today,
					clientEventId: command.input.clientEventId ?? null,
					xp: 0,
					createdAt: now
				});
				return;
			}
			patch(replica, node.id, { status: "done", completedAt: now }, now);
			return;
		}

		case "uncompleteNode": {
			const node = replica.nodes.get(command.input.nodeId);
			if (!node) {
				return;
			}
			if (node.role === "recurring") {
				const on = command.input.on ?? today;
				const mine = [...replica.completions.values()]
					.filter((c) => c.nodeId === node.id && c.completedOn === on)
					.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
				const last = mine[mine.length - 1];
				if (last) {
					replica.completions.delete(last.id);
				}
				return;
			}
			patch(replica, node.id, { status: "open", completedAt: null }, now);
			return;
		}

		case "ensureDay": {
			const id = dayNodeId(command.input.date);
			if (replica.nodes.has(id)) {
				return;
			}
			// Позиция — по дате: дни в Календаре лежат хронологически, в том числе
			// когда день заводится задним числом.
			const days = [...replica.nodes.values()]
				.filter((n) => n.role === "day" && n.dayDate !== null)
				.sort((a, b) => (a.dayDate! < b.dayDate! ? -1 : 1));
			let prev: string | null = null;
			let next: string | null = null;
			for (const day of days) {
				if (day.dayDate! < command.input.date) {
					prev = day.position;
				} else if (next === null) {
					next = day.position;
				}
			}
			replica.nodes.set(id, {
				...blankNode({
					id,
					parentId: SYSTEM_NODE_IDS.calendar,
					position: generateKeyBetween(prev, next),
					title: command.input.date,
					now
				}),
				role: "day",
				status: null,
				dayDate: command.input.date
			});
			return;
		}

		case "setTimezone":
			// Настройка живёт вне реплики — её везёт `useTimezone`, а не дерево.
			return;
	}
}
